/* eslint-disable react-hooks/exhaustive-deps, react/prop-types */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import { useNavigate, Link } from 'react-router-dom';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import Pagination from 'react-js-pagination';
import Navbar from '../Navbar';
import Header from '../Header';
import baseUrl from '../BaseUrl';
import EmptyState from '../active-users/states/EmptyState';
import ErrorState from '../active-users/states/ErrorState';
import TableSkeleton from '../active-users/states/TableSkeleton';
import '../active-users/active-users.css';
import '../free-grants/free-grants.css';
import './bigquery-install-events.css';

/**
 * BigQuery Events (095) — /bigquery-install-events
 *
 * The screen is titled "BigQuery Events"; the route, this component, the API paths and the
 * feature itself all still say `install-events`, so searching either name finds it.
 *
 * Two halves, deliberately separate:
 *   Sync   POST /admin/api/bigquery/install-events/sync    reads BigQuery, writes local storage.
 *          Billed per press, so the button disables while it runs and the server enforces a
 *          per-admin cooldown (429). Its answer is the fetched / inserted / already-stored count.
 *   Table  GET  /admin/api/bigquery/install-events/stored  reads ONLY the local table, so
 *          paging, filtering and re-reading cost nothing and never touch BigQuery.
 *
 * The table shows the member's NAME, linking to their User 360 page (/user-detail/:encodedUserId,
 * btoa of the id, as every other screen does it); the
 * install id is not a column — it is a GA4 internal id nobody reads — but the search box still
 * matches it, along with the name, the user id and the device id.
 *
 * The date rules come from the export, not from taste: a GA4 day is a UTC day, today's rows are
 * provisional (streaming table) so only completed days can be stored, and one sync covers at
 * most 7 days. The presets below therefore all END YESTERDAY — the shared DateRangeBar presets
 * (Last 30 / This month) would produce ranges the server refuses.
 */

const LIMIT = 50;
const STATE_KEY = 'bigQueryInstallEventsState';
const DAY_MS = 86400000;

// Kept in step with INSTALL_EVENTS + DASHBOARD_EVENT in services/bigQueryInstallEventsService.js.
const EVENT_NAMES = [
  'first_open', 'session_start', 'pauser_app_install',
  'custom_login_start', 'custom_login_start_firebase',
  'custom_sign_up_success', 'custom_login_success',
  'custom_login_error', 'custom_login_network_error', 'custom_sign_up_error',
  'custom_login_cancel', 'dashboard_view',
];

const PRESETS = [
  { key: 'yesterday', label: 'Yesterday', days: 1 },
  { key: 'last3', label: 'Last 3 days', days: 3 },
  { key: 'last7', label: 'Last 7 days', days: 7 },
];

/** 'YYYY-MM-DD' for a Date, read in UTC — GA4 days are UTC days. */
const fmtUtc = (date) => new Date(date).toISOString().slice(0, 10);
const utcDayStart = (ms) => new Date(Math.floor(ms / DAY_MS) * DAY_MS);
const yesterdayUtc = (now = Date.now()) => utcDayStart(now - DAY_MS);
const presetRange = (days, now = Date.now()) => {
  const end = yesterdayUtc(now);
  return { fromStr: fmtUtc(end.getTime() - (days - 1) * DAY_MS), toStr: fmtUtc(end) };
};
// GA4 writes the platform in caps; the API filters on that value.
const PLATFORMS = [{ value: 'ANDROID', label: 'Android' }, { value: 'IOS', label: 'iOS' }];
const platformLabel = (p) => PLATFORMS.find((x) => x.value === String(p || '').toUpperCase())?.label || p || '-';

const dmy = (iso) => (iso ? iso.split('-').reverse().join('/') : '');
const num = (n) => Number(n || 0).toLocaleString();

const BigQueryInstallEvents = ({ now = Date.now }) => {
  const navigate = useNavigate();

  const initial = presetRange(1, now());
  const [fromDate, setFromDate] = useState(initial.fromStr);
  const [toDate, setToDate] = useState(initial.toStr);
  const [preset, setPreset] = useState('yesterday');
  const [eventName, setEventName] = useState('');
  const [platform, setPlatform] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);

  const [token, setToken] = useState('');
  const [expire, setExpire] = useState('');
  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, pages: 0 });
  const [summary, setSummary] = useState({ total: 0, installs: 0, syncedDays: [] });
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState(null); // { kind: 'ok' | 'warn' | 'error', text }

  const axiosJWT = axios.create();
  axiosJWT.interceptors.request.use(async (config) => {
    if (expire * 1000 < new Date().getTime()) {
      const res = await axios.get(`${baseUrl}/admin/api/token`);
      config.headers.Authorization = `Bearer ${res.data.accessToken}`;
      setToken(res.data.accessToken);
      setExpire(jwtDecode(res.data.accessToken).exp);
    }
    return config;
  }, (err) => Promise.reject(err));

  const refreshToken = async () => {
    try {
      const res = await axios.get(`${baseUrl}/admin/api/token`);
      setToken(res.data.accessToken);
      setExpire(jwtDecode(res.data.accessToken).exp);
    } catch (err) {
      if (err.response) { navigate('/'); window.location.reload(); }
    }
  };

  useEffect(() => {
    try {
      const saved = JSON.parse(sessionStorage.getItem(STATE_KEY) || 'null');
      if (saved) {
        setFromDate(saved.fromDate || initial.fromStr);
        setToDate(saved.toDate || initial.toStr);
        setPreset(saved.preset ?? null);
        setEventName(saved.eventName || '');
        setPlatform(saved.platform || '');
        setSearch(saved.search || '');
        setPage(saved.page || 1);
      }
    } catch { /* storage may be blocked — the defaults above still stand */ }
    refreshToken();
  }, []);

  useEffect(() => {
    try {
      sessionStorage.setItem(STATE_KEY, JSON.stringify({ fromDate, toDate, preset, eventName, platform, search, page }));
    } catch { /* ignore */ }
  }, [fromDate, toDate, preset, eventName, platform, search, page]);

  useEffect(() => { if (token) loadStored(page); }, [token]);

  const auth = () => ({ headers: { Authorization: `Bearer ${token}` } });

  /** Reads the LOCAL table only — never BigQuery. */
  const loadStored = async (p = page, over = {}) => {
    setLoading(true);
    setError('');
    const from = over.from ?? fromDate;
    const to = over.to ?? toDate;
    const ev = over.eventName ?? eventName;
    const pf = over.platform ?? platform;
    const q = over.search ?? search;
    try {
      const qs = new URLSearchParams({ from_date: from, to_date: to, page: String(p), limit: String(LIMIT) });
      if (ev) qs.set('event_name', ev);
      if (pf) qs.set('platform', pf);
      if (q) qs.set('search', q);
      const res = await axiosJWT.get(`${baseUrl}/admin/api/bigquery/install-events/stored?${qs}`, auth());
      const data = res.data?.data || {};
      setRows(data.events || []);
      setPagination(data.pagination || { total: 0, pages: 0 });
      setSummary(data.summary || { total: 0, installs: 0, syncedDays: [] });
    } catch (err) {
      setRows([]);
      setError(err?.response?.status === 401 ? 'auth' : 'load');
    } finally {
      setLoading(false);
    }
  };

  /** The only write on this screen: reads BigQuery and stores what is not stored yet. */
  const runSync = async () => {
    setSyncing(true);
    setNotice(null);
    try {
      const res = await axiosJWT.post(
        `${baseUrl}/admin/api/bigquery/install-events/sync`,
        { from_date: fromDate, to_date: toDate },
        auth(),
      );
      const d = res.data?.data || {};
      const t = d.totals || {};
      const range = d.from === d.to ? dmy(d.from) : `${dmy(d.from)} – ${dmy(d.to)}`;
      const missing = (d.daysMissing || []).map(dmy).join(', ');
      setNotice({
        kind: missing ? 'warn' : 'ok',
        text: `Synced ${range}: fetched ${num(t.fetched)} · inserted ${num(t.inserted)} · already stored ${num(t.skipped)}.`
          + (missing ? ` Not ready yet in BigQuery: ${missing} — try again later.` : ''),
      });
      setPage(1);
      await loadStored(1);
    } catch (err) {
      // 429 is the per-admin cooldown and 400 is a rejected range: both carry a message worth
      // reading. Anything else is a fault the server has already reduced to a safe sentence.
      const status = err?.response?.status;
      if (status === 401) { setError('auth'); return; }
      setNotice({
        kind: 'error',
        text: err?.response?.data?.message || 'The sync could not be completed. Please try again.',
      });
    } finally {
      setSyncing(false);
    }
  };

  const applyPreset = (p) => {
    const { fromStr, toStr } = presetRange(p.days, now());
    setPreset(p.key);
    setFromDate(fromStr);
    setToDate(toStr);
    setPage(1);
    loadStored(1, { from: fromStr, to: toStr });
  };

  const handleDate = (which) => (date) => {
    if (!date) return;
    const iso = fmtUtc(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    setPreset(null);
    if (which === 'from') setFromDate(iso); else setToDate(iso);
  };

  const handleSearch = () => { setPage(1); loadStored(1); };

  const clearFilters = () => {
    const { fromStr, toStr } = presetRange(1, now());
    setPreset('yesterday');
    setFromDate(fromStr);
    setToDate(toStr);
    setEventName('');
    setPlatform('');
    setSearch('');
    setPage(1);
    setNotice(null);
    loadStored(1, { from: fromStr, to: toStr, eventName: '', platform: '', search: '' });
  };

  const handlePage = (p) => { setPage(p); loadStored(p); };

  const maxPickerDate = new Date(yesterdayUtc(now()).getTime());
  const asDate = (iso) => (iso ? new Date(`${iso}T00:00:00`) : null);
  const hasFilters = Boolean(eventName || platform || search || preset !== 'yesterday');
  const dayCount = fromDate && toDate
    ? Math.round((Date.parse(`${toDate}T00:00:00Z`) - Date.parse(`${fromDate}T00:00:00Z`)) / DAY_MS) + 1
    : 0;
  const rangeTooLong = dayCount > 7 || dayCount < 1;

  return (
    <div className="panelbox">
      <Navbar />
      <div className="right-panel">
        <Header name="Pauser" />
        <div className="content-page">
          <div className="au-head">
            <span className="au-breadcrumb">Growth &amp; engagement › <b>BigQuery events</b></span>
            <span className="au-route">/bigquery-install-events</span>
            <span className="au-spacer" />
            <span className="au-back"><Link to="/dashboard">← Back</Link></span>
          </div>
          <h1 className="au-title">BigQuery Events</h1>

          {error === 'auth' ? (
            <ErrorState title="Your session expired" message="Please sign in again to continue." onRetry={() => navigate('/')} />
          ) : (
            <>
              <div className="bq-filters" data-testid="bq-filters">
                <div className="au-presets" role="group" aria-label="Quick date ranges">
                  {PRESETS.map((p) => (
                    <button
                      key={p.key}
                      type="button"
                      className={`au-preset${preset === p.key ? ' au-preset--active' : ''}`}
                      aria-pressed={preset === p.key}
                      onClick={() => applyPreset(p)}
                    >
                      {p.label}
                    </button>
                  ))}
                </div>
                <div className="bq-filters__grid">
                  <div className="au-field">
                    <span className="au-field__label">From (UTC)</span>
                    <DatePicker
                      className="au-date-input"
                      selected={asDate(fromDate)}
                      onChange={handleDate('from')}
                      dateFormat="dd/MM/yyyy"
                      maxDate={maxPickerDate}
                      placeholderText="From date"
                      aria-label="From date"
                    />
                  </div>
                  <div className="au-field">
                    <span className="au-field__label">To (UTC)</span>
                    <DatePicker
                      className="au-date-input"
                      selected={asDate(toDate)}
                      onChange={handleDate('to')}
                      dateFormat="dd/MM/yyyy"
                      maxDate={maxPickerDate}
                      placeholderText="To date"
                      aria-label="To date"
                    />
                  </div>
                  <div className="au-field">
                    <span className="au-field__label">Event</span>
                    <select
                      className="au-select"
                      value={eventName}
                      aria-label="Event filter"
                      onChange={(e) => { setEventName(e.target.value); setPage(1); loadStored(1, { eventName: e.target.value }); }}
                    >
                      <option value="">All events</option>
                      {EVENT_NAMES.map((n) => <option key={n} value={n}>{n}</option>)}
                    </select>
                  </div>
                  <div className="au-field">
                    <span className="au-field__label">Platform</span>
                    <select
                      className="au-select"
                      value={platform}
                      aria-label="Platform filter"
                      onChange={(e) => { setPlatform(e.target.value); setPage(1); loadStored(1, { platform: e.target.value }); }}
                    >
                      <option value="">All platforms</option>
                      {PLATFORMS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
                    </select>
                  </div>
                  <div className="au-field">
                    <span className="au-field__label">Search</span>
                    <input
                      className="au-input"
                      type="text"
                      placeholder="Name, install, user or device id"
                      value={search}
                      aria-label="Search stored events"
                      onChange={(e) => setSearch(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') handleSearch(); }}
                    />
                  </div>
                </div>
                <div className="bq-filters__actions">
                  <button type="button" className="au-btn au-btn--primary" onClick={handleSearch}>Search</button>
                  <button type="button" className="au-btn au-btn--ghost" onClick={clearFilters}>Clear</button>
                  <span className="au-spacer" />
                  <button
                    type="button"
                    className="au-btn au-btn--primary bq-sync"
                    data-testid="bq-sync"
                    onClick={runSync}
                    disabled={syncing || rangeTooLong}
                    title={rangeTooLong ? 'Pick a range of 1 to 7 completed days' : 'Read these days from BigQuery and store what is new'}
                  >
                    {syncing ? 'Syncing…' : '⟳ Sync from BigQuery'}
                  </button>
                </div>
              </div>

              {/* The standing note explaining UTC days, the 7-day cap and BigQuery billing was
                  removed at the client's request. The RULES it described still hold and are still
                  enforced: the pickers offer no day later than yesterday (maxPickerDate), and a
                  range over seven days cannot be sent. Only the over-long warning speaks up now,
                  and only when it applies. */}
              {rangeTooLong && (
                <p className="au-range-note" data-testid="bq-range-note">
                  <b className="bq-warn">This range is {dayCount} days — narrow it to 7 or fewer.</b>
                </p>
              )}

              {notice && (
                <div
                  className={`fg-alert fg-alert--${notice.kind}`}
                  role="status"
                  data-testid="bq-notice"
                  style={{ marginBottom: 12 }}
                >
                  {notice.text}
                </div>
              )}

              <div className="au-cards">
                <div className="au-card">
                  <div className="au-card__label">Rows stored</div>
                  <div className="au-card__value tnum">{num(summary.total)}</div>
                  <div className="au-card__sub">in {dmy(fromDate)} – {dmy(toDate)}</div>
                </div>
                <div className="au-card">
                  <div className="au-card__label">Installs</div>
                  <div className="au-card__value tnum">{num(summary.installs)}</div>
                  <div className="au-card__sub">distinct GA4 installations</div>
                </div>
                <div className="au-card">
                  <div className="au-card__label">Days synced</div>
                  <div className="au-card__value tnum">{(summary.syncedDays || []).length} / {dayCount > 0 ? dayCount : 0}</div>
                  <div className="au-card__sub">
                    {(summary.syncedDays || []).length
                      ? `last sync ${summary.syncedDays[summary.syncedDays.length - 1].syncedAt} UTC`
                      : 'nothing synced in this range yet'}
                  </div>
                </div>
              </div>

              {loading ? (
                <div className="au-panel"><TableSkeleton rows={8} /></div>
              ) : error === 'load' ? (
                <ErrorState message="We couldn’t load the stored install events. Please try again." onRetry={() => loadStored(page)} />
              ) : rows.length === 0 ? (
                <EmptyState
                  title={(summary.syncedDays || []).length ? 'No events in this range' : 'This range is not synced yet'}
                  hasFilters={hasFilters}
                  onClear={clearFilters}
                  message={(summary.syncedDays || []).length
                    ? 'These days are synced; no stored event matches the current filters.'
                    : 'Press “Sync from BigQuery” to copy these days into local storage.'}
                />
              ) : (
                <div className="au-panel">
                  <div className="au-panel__head">
                    <h4 className="au-panel__title">Stored events</h4>
                    <span className="au-spacer" />
                    <span className="au-legend">newest first</span>
                  </div>
                  <div className="au-table-wrap">
                    <table className="au-table au-mau-table bq-table" data-testid="bq-table">
                      <thead>
                        <tr>
                          <th>No</th>
                          <th>Event</th>
                          <th>Time (UTC)</th>
                          <th>Platform</th>
                          <th>User</th>
                          <th>Device id</th>
                          <th>App version</th>
                          <th>Device</th>
                          <th>OS</th>
                          <th>Country</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rows.map((r, i) => (
                          <tr key={r.id}>
                            <td data-label="No">{(page - 1) * LIMIT + i + 1}</td>
                            <td data-label="Event"><span className="bq-event">{r.eventName}</span></td>
                            <td data-label="Time (UTC)" className="tnum">{r.eventTimeUtc}</td>
                            <td data-label="Platform">{platformLabel(r.platform)}</td>
                            {/* The member's name once signed in, linking to their User 360 page.
                                A user id we cannot resolve to a member is shown — and linked — as
                                itself: an empty cell would hide a real sign-in. */}
                            <td data-label="User" className="au-ellipsis">
                              {r.userId ? (
                                <Link
                                  className="au-link"
                                  to={`/user-detail/${btoa(String(r.userId))}`}
                                  title="Open user profile"
                                  data-testid="user-detail-link"
                                >
                                  {r.userName || r.userId}
                                </Link>
                              ) : '-'}
                            </td>
                            <td data-label="Device id" className="au-ellipsis">{r.deviceId || '-'}</td>
                            <td data-label="App version">{r.appVersion || '-'}</td>
                            <td data-label="Device" className="au-ellipsis">{r.deviceModel || '-'}</td>
                            <td data-label="OS">{r.osVersion || '-'}</td>
                            <td data-label="Country" className="au-ellipsis">{r.country || '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {pagination.total > LIMIT && (
                    <Pagination
                      activePage={page}
                      itemsCountPerPage={LIMIT}
                      totalItemsCount={pagination.total}
                      pageRangeDisplayed={5}
                      onChange={handlePage}
                    />
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default BigQueryInstallEvents;
